import { computed, type Ref } from 'vue'
import { useHead } from '@unhead/vue'
import type {
    StorefrontCalendarScalar,
    StorefrontCatalogScalar
} from '~/resources/scalars/storefront-catalog-scalar'

type StorefrontSeoNode =
    | StorefrontCatalogScalar
    | StorefrontCalendarScalar
    | null

export function useStorefrontCatalogCalendarSeo(
    node: Readonly<Ref<StorefrontSeoNode>>
) {
    const description = computed(() => {
        return getStorefrontDescription(node.value)
    })

    useHead(() => {
        // Build the full head input reactively so unhead's types see an
        // array value instead of a computed wrapper around the array.
        if (description.value === null) {
            return {
                meta: []
            }
        }

        return {
            meta: [
                {
                    key: 'description',
                    name: 'description',
                    content: description.value
                },
                {
                    key: 'og:description',
                    property: 'og:description',
                    content: description.value
                },
                {
                    key: 'twitter:description',
                    name: 'twitter:description',
                    content: description.value
                }
            ]
        }
    })
}

function getStorefrontDescription(node: StorefrontSeoNode) {
    if (node === null) {
        return null
    }

    // The explicit field lets operators tune snippets without changing visible
    // storefront copy. Existing content remains the fallback for older configs.
    const metaDescription = cleanHtmlForMetaDescription(
        node.metaDescription ?? ''
    )
    if (metaDescription !== '') {
        return metaDescription
    }

    const summary = cleanHtmlForMetaDescription(node.shortSummaryMarkup ?? '')
    if (summary !== '') {
        return summary
    }

    const body = cleanHtmlForMetaDescription(node.body ?? '')
    if (body === '') {
        return null
    }

    return body
}

function cleanHtmlForMetaDescription(html: string) {
    const text = html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ' ')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/g, "'")
        .replace(/&rsquo;/gi, "'")
        .replace(/&lsquo;/gi, "'")
        .replace(/&rdquo;/gi, '"')
        .replace(/&ldquo;/gi, '"')
        .replace(/&hellip;/gi, '...')
        .replace(/\s+/g, ' ')
        .trim()

    return truncateMetaDescription(text)
}

function truncateMetaDescription(text: string) {
    const maxLength = 160
    if (text.length <= maxLength) {
        return text
    }

    const clipped = text.slice(0, maxLength + 1).trim()
    const lastSpace = clipped.lastIndexOf(' ')
    const truncationPoint = lastSpace > 80 ? lastSpace : maxLength

    return clipped
        .slice(0, truncationPoint)
        .replace(/[.,;:!?-]+$/g, '')
        .trim()
        .concat('...')
}
